import { FromCarActionBase } from '../from-car-action.base';
import { BookingParams, BookingPosition, BookingResult, StockTargetType } from '../../types';

/**
 * Action für alle Buchungen mit Quelle = KFZ in der Unified-Welt.
 *
 * Mapping zu Backend-Movement-Types:
 *   - `CAR` → `CarRelocationMovement` (zweiphasig)
 *   - `BUFFERSTOCK`/`MACHINE` → `CarTransferMovement`
 *   - `TRASH_BIN` → keine valide Direction (mit Bufferstock läuft die
 *     Verschrottung über den Bufferstock-Disposal, nicht direkt aus dem KFZ)
 */
export class UnifiedFromCarAction extends FromCarActionBase {
  /**
   * Routet die Buchung nach `targetType` auf die gültigen KFZ-Quell-Richtungen
   * (KFZ/Bufferstock/Maschine); Müll, Verbrauch und BufferstockDisposal werden
   * abgelehnt.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult der gewählten Richtung bzw. Fehler bei unzulässigem Ziel
   */
  public async execute(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    this.debugInfo('[from-car:unified]', position, params);
    switch (params.context.booking.targetType) {
      case StockTargetType.CAR:
        return this.toCar(position, params);
      case StockTargetType.BUFFERSTOCK:
        return this.toBufferstock(position, params);
      case StockTargetType.MACHINE:
        return this.toMachine(position, params);
      case StockTargetType.TRASH_BIN:
        return this.failure('KFZ → Müll ist keine valide Direction');
      case StockTargetType.CONSUMPTION_BIN:
        return this.failure('KFZ → Verbrauch ist keine valide Direction');
      case StockTargetType.BUFFERSTOCK_DISPOSAL:
        return this.failure('KFZ → BufferstockDisposal ist keine valide Direction');
      default:
        return this.exhaustiveFailure(params.context.booking.targetType);
    }
  }

  /**
   * KFZ → Bufferstock: Entnahme aus KFZ / Befüllung des Bufferstocks.
   * Erzeugt ein CarTransferMovement.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlender targetId, fehlendem Fahrzeuglager, amount <= 0 oder Speicher-/Update-Fehlschlag
   */
  protected async toBufferstock(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;

    if (booking.targetId === undefined) {
      return this.failure('KFZ->Bufferstock: targetId (Bufferstock) fehlt');
    }
    // Ohne lokales Fahrzeuglager keine Basis → früh ablehnen
    if (!adapters.car.hasLocalInventory()) {
      return this.failure(
        'KFZ->Bufferstock: kein lokales Fahrzeuglager vorhanden',
      );
    }

    // Offline gibt es kein synchrones Backend-Ja/Nein. Was das Backend beim
    // Speichern der Queue ablehnen würde, darf gar nicht erst in die Queue.
    if (position.amount <= 0) {
      return this.failure('KFZ->Bufferstock: amount muss > 0 sein');
    }

    // 1. Warehouse: CarTransferMovement.
    //    Zuerst, weil dieser Schritt abgelehnt werden kann (Validierung/Netz);
    //    schlägt er fehl, bleibt das Auto unangetastet. Idempotent via client_id.
    const movement = adapters.movement.build(
      position,
      booking,
      params.clientIds.idFor(position),
    );
    const queued = await adapters.movement.enqueue(movement);
    if (!queued.value) {
      return this.failure(
        'CarTransferMovement konnte nicht in der Queue gespeichert werden',
        queued.retryable,
      );
    }
    // 2. Bufferstock lokal: Ziel erhöhen. Optimistisch (das Backend bucht beim
    //    Drainen ebenfalls, der Down-Sync gleicht ab), damit Folgebuchungen den
    //    richtigen Bestand sehen.
    const bufferstockUpdated =
      await adapters.bufferstock.updatePositionAmountLocal(
        position,
        booking.targetId,
        +position.amount,
      );
    if (!bufferstockUpdated) {
      return this.failure(
        `Bufferstock ${booking.targetId} konnte nicht aktualisiert werden`,
      );
    }
    // 3. Car: lokales Fahrzeuglager verringern (Fahrzeug gibt amount ab).
    const carUpdated = await adapters.car.updatePositionAmountLocal(
      position,
      -position.amount,
    );
    if (!carUpdated) {
      return this.failure('Car-Bestand konnte nicht aktualisiert werden');
    }
    return this.success();
  }

  /**
   * KFZ → Maschine: welt-übergreifende Befüllung, wenn KFZ- und
   * Bufferstock-Welt zusammen aktiv sind (KFZ gibt ab, Maschinenlager
   * bekommt). Erzeugt ein CarTransferMovement auf das StockObject des
   * Maschinenlagers.
   *
   * @param position Zu buchende Position (article_id und amount; amount darf negativ sein, nur nicht 0)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlender targetId, fehlendem Fahrzeuglager, amount == 0, fehlendem Maschinenlager oder Speicher-/Update-Fehlschlag
   */
  protected async toMachine(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;

    if (booking.targetId === undefined) {
      return this.failure('KFZ->Maschine: targetId (Maschine) fehlt');
    }
    // Ohne lokales Fahrzeuglager keine Basis → früh ablehnen
    if (!adapters.car.hasLocalInventory()) {
      return this.failure(
        'KFZ->Maschine: kein lokales Fahrzeuglager vorhanden',
      );
    }
    // amount darf negativ sein (verrechneter Take-Back-Saldo, Rückgabe ans KFZ);
    // nur 0 ist sinnlos.
    if (position.amount === 0) {
      return this.failure('KFZ->Maschine: amount darf nicht 0 sein');
    }

    // Die StockObject-Seite des Movements braucht die StockObject-ID des
    // Maschinenlagers, nicht die Maschinen-ID (booking.targetId). Das lokale
    // Maschinen-Update unten läuft weiter über die Maschinen-ID (der
    // MachineAdapter löst das StockObject selbst auf).
    const machineId = booking.targetId;
    const stockObjectId =
      await adapters.machine.findStockObjectIdLocal(machineId);
    if (stockObjectId === null) {
      return this.failure(
        `KFZ->Maschine: Maschine ${machineId} hat kein Lager (StockObject)`,
      );
    }

    // 1. Warehouse: CarTransferMovement auf das Maschinen-StockObject.
    //    Zuerst, weil dieser Schritt abgelehnt werden kann; schlägt er fehl,
    //    bleibt das Auto unangetastet. Idempotent via client_id.
    const movement = adapters.movement.build(
      position,
      { ...booking, targetId: stockObjectId },
      params.clientIds.idFor(position),
    );
    const queued = await adapters.movement.enqueue(movement);
    if (!queued.value) {
      return this.failure(
        'CarTransferMovement konnte nicht in der Queue gespeichert werden',
        queued.retryable,
      );
    }
    // 2. Optional in die Transaction einreihen (für den Auftrags-Rollback).
    //    Beim Abbruch bucht der Modul-Rollback ein invertiertes
    //    CarTransferMovement gegen und stellt den KFZ-Mirror wieder her.
    const rollbackFailure = this.enqueueForRollback(position, params);
    if (rollbackFailure) {
      return rollbackFailure;
    }
    // 3. Maschinenlager lokal erhöhen (Ziel bekommt amount). Optimistisch,
    //    der Down-Sync gleicht ab.
    const machineUpdated = await adapters.machine.updatePositionAmountLocal(
      position,
      machineId,
      +position.amount,
    );
    if (!machineUpdated) {
      return this.failure(
        `Maschine ${machineId} konnte nicht aktualisiert werden`,
      );
    }
    // 4. Car: lokales Fahrzeuglager verringern (Fahrzeug gibt amount ab).
    const carUpdated = await adapters.car.updatePositionAmountLocal(
      position,
      -position.amount,
    );
    if (!carUpdated) {
      return this.failure('Car-Bestand konnte nicht aktualisiert werden');
    }
    return this.success();
  }
}
